import { defineStore } from "pinia";
import { RouteLocationNormalized } from "vue-router";
import { useUser } from "@/store/pinia/userStore";
import { ApplicationCurrentProduction } from "@/models/ApplicationCurrentProduction";
import { BackstagePlan } from "@/models/subscriptions/CreateSubscriptionDto";
import { BackstageEntitlementsDto } from "@/models/subscriptions/BackstageEntitlementsDto";

export const useApplication = defineStore("application", {
	state: () => {
		return {
			isLoaded: false,
			isAuthenticated: false,
			route: {} as RouteLocationNormalized | { name: "" } as any,
			pushNotificationPermission: "not-set",
			ui: {
				isDetails: false,
				activeRequestsTab: "requests",
				activeGlobalTab: "Home",
				activeEntityTab: "Dashboard",
				isEntityMode: false,
				isUserSettingsMode: false,
				activeUserSettingTab: "General",
				entityName: "",
				header: {
					title: "",
				},
				entity: {
					people: {
						tab: "members",
						peopleCount: 0,
					},
					reports: {
						templatesCount: 0,
					},
					productions: {
						tab: "active",
					},
					drive: {
						tab: "list",
						canManage: false,
						currentFolderName: "",
					},
				},
				people: {
					tab: "members", //members, groups, requests
				},
				user: {
					communications: {
						tab: "",
					},
					scheduling: {
						tab: "",
					},
					memberships: {
						tab: "memberships", //memberships, requests
					},
					mobileSettingsTab: "unset",
				},
				calls: {
					selected: 0, // This is used with the call selection table, to link to the toolbar button
				},
			},
		};
	},
	actions: {
		setLoadingState: function(loadingState: boolean) {
			this.isLoaded = loadingState;
		},
		setPeopleCount: function(count: number) {
			this.ui.entity.people.peopleCount = count;
		},
		setReportTemplatesCount: function(count: number) {
			this.ui.entity.reports.templatesCount = count;
		},
		setAuthenticatedState: function(authenticatedState: boolean) {
			this.isAuthenticated = authenticatedState;
		},
		setRoute(route) {
			this.route = route;
		},
		changeGlobalTab(tabName: string) {
			this.ui.activeGlobalTab = tabName;
			this.ui.isUserSettingsMode = tabName === "Settings";
		},
		changeEntityTab(tabName: string) {
			this.ui.activeEntityTab = tabName;
		},
		changeUserSettingTab(tabName: string) {
			this.ui.activeUserSettingTab = tabName;
		},
		changeEntityMode(isEntityMode: boolean) {
			this.ui.isEntityMode = isEntityMode;
		},
		updateEntityName(name: string) {
			this.ui.entityName = name;
		},
		changePeopleTab(tab: string) {
			this.ui.people.tab = tab;
		},
		changeEntityProductionsTab(tab: string) {
			this.ui.entity.productions.tab = tab;
		},
		changeUserMembershipsTab(tab: string) {
			this.ui.user.memberships.tab = tab;
		},
		setUserMobileSettingsTab(tab) {
			this.ui.user.mobileSettingsTab = tab;
		},
	},
	getters: {
		peopleCurrentTab: (state) => {
			return state.ui.people.tab;
		},
		userMembershipsCurrentTab: (state) => {
			return state.ui.user.memberships.tab;
		},
		isEntityMode: (state) => {
			return state.route.name.indexOf("production") > -1 || state.route.name.indexOf("organization") > -1;
		},
		isProduction: (state) => {
			return state.route.name.indexOf("production") > -1 && state.route.name.indexOf("productions") < 0;
		},
		isOrganization: (state) => {
			return state.route.name.indexOf("organization") > -1;
		},
		isArchived(state) {
			// Create an instance of useUser()
			const userStore = useUser();

			// Check if the route is for a production
			if (state.route.name.indexOf("production") > -1) {
				const productionId = (state.route as RouteLocationNormalized).params["productionId"] as string;
				// Find the production in the user's productions array
				const production = userStore.productions.find(p => p.productionId === productionId);
				// Return whether the production's entityStatus indicates it's archived
				return production && production.entityStatus === 2;  // Assuming 1 indicates archived
			}
			// Check if the route is for an organization
			else if (state.route.name.indexOf("organization") > -1) {
				const organizationId = (state.route as RouteLocationNormalized).params["organizationId"] as string;
				// Find the organization in the user's organizations array
				const organization = userStore.organizations.find(o => o.organizationId === organizationId);
				// Return whether the organization's entityStatus indicates it's archived
				return organization && organization.entityStatus === 2;  // Assuming 1 indicates archived
			}
			// Return false if the route is for neither
			return false;
		},
		productionId: (state): string => {
			if (state.route.name.indexOf("production") > -1) {
				return (state.route as RouteLocationNormalized).params["productionId"] as string;
			}
			return "";
		},
		organizationId: (state): string => {
			if (state.route.name.indexOf("organization") > -1) {
				return (state.route as RouteLocationNormalized).params["organizationId"] as string;
			}
			return "";
		},
		currentProduction: (state): ApplicationCurrentProduction => {
			if (state.route.name.indexOf("production") > -1) {
				const user = useUser();
				const productionId = (state.route as RouteLocationNormalized).params["productionId"] as string;
				const production = user.productionData.find((p) => p.productionId === productionId);
				const currentProduction = {} as ApplicationCurrentProduction;

				currentProduction.productionId = productionId;
				currentProduction.organizationId = production?.organizationId;
				currentProduction.plan = production?.subscription?.plan;
				currentProduction.billingActive = production?.subscription?.billingActive;
				currentProduction.planName = BackstagePlan[production?.subscription?.plan];
				currentProduction.isFreePlan = production?.subscription?.plan === BackstagePlan.Free;
				currentProduction.subscriptionStatus = production?.subscription?.status;
				currentProduction.entitlements = production?.subscription?.entitlements;
				currentProduction.trialDaysRemaining = production?.subscription?.trialDaysRemaining;
				currentProduction.hasTrialOffer = user.organizationData.find(o => o.organizationId === production?.organizationId)?.subscriptionOffers.some(x => x.type === 3 && !x.redeemed);
				currentProduction.offers = user.organizationData.find(o => o.organizationId === production?.organizationId)?.subscriptionOffers;
				currentProduction.peopleCount = state.ui.entity.people.peopleCount;
				currentProduction.reportTemplatesCount = state.ui.entity.reports.templatesCount;
				currentProduction.quantity = production?.subscription?.quantity;

				return currentProduction;
			}
			return { entitlements: { hasReportBuilder: false } as BackstageEntitlementsDto } as ApplicationCurrentProduction;
		},
		currentOrganization: (state): ApplicationCurrentProduction => {
			if (state.route.name.indexOf("organization") > -1) {
				const user = useUser();
				const organizationId = (state.route as RouteLocationNormalized).params["organizationId"] as string;
				const organization = user.organizationData.find((o) => o.organizationId === organizationId);
				const currentOrganization = {} as ApplicationCurrentProduction;

				currentOrganization.organizationId = organizationId;
				currentOrganization.plan = organization?.backstageSubscription.plan;
				currentOrganization.billingActive = organization?.backstageSubscription.billingActive;
				currentOrganization.planName = BackstagePlan[organization?.backstageSubscription.plan];
				currentOrganization.isFreePlan = organization?.backstageSubscription.plan === BackstagePlan.Free;
				currentOrganization.subscriptionStatus = organization?.backstageSubscription.status;
				currentOrganization.entitlements = organization?.backstageSubscription.entitlements;
				currentOrganization.trialDaysRemaining = organization?.backstageSubscription.trialDaysRemaining;
				currentOrganization.hasTrialOffer = organization?.subscriptionOffers.some(x => x.type === 3 && !x.redeemed);
				currentOrganization.offers = organization?.subscriptionOffers;
				currentOrganization.quantity = organization?.backstageSubscription.quantity;

				return currentOrganization;
			}
			return { entitlements: { hasReportBuilder: false } as BackstageEntitlementsDto } as ApplicationCurrentProduction;
		},
		productionUpgradeButtonCta(): string {
			const currentProduction = this.currentProduction;
			if (this.isProduction) {
				if (currentProduction?.plan == null) {
					return "";
				}

				if (currentProduction.offers && currentProduction.offers.length > 0 && currentProduction.offers.some(x => !x.redeemed)) {
					return "Start Trial";
				}

				return "Upgrade Now";
			} else {
				return this.organizationUpgradeButtonCta
			}
		},
		organizationUpgradeButtonCta(): string {
			const currentOrganization = this.currentOrganization;

			if (currentOrganization?.plan == null) {
				return "";
			}

			if (currentOrganization.offers && currentOrganization.offers.length > 0 && currentOrganization.offers.some(x => !x.redeemed)) {
				return "Start Trial";
			}

			return "Upgrade Now";
		},
		folderId: (state): string => {
			if (state.route.name.indexOf("drive") > -1) {
				if ((state.route as RouteLocationNormalized).params["folderId"]) {
					return (state.route as RouteLocationNormalized).params["folderId"] as string;
				} else {
					if (state.route.name.indexOf("production") > -1) {
						return (state.route as RouteLocationNormalized).params["productionId"] as string;
					}
					return "need production id";
				}
			}
			return "";
		},
		isRootFolder: (state): string => {
			if (state.route.name.indexOf("drive") > -1) {
				if ((state.route as RouteLocationNormalized).params["folderId"]) {
					return (state.route as RouteLocationNormalized).params["folderId"] as string;
				} else {
					if (state.route.name.indexOf("production") > -1) {
						return "";
					}
					return "need production id";
				}
			}
			return "";
		},
	},
});
